import { type ApolloClient, useApolloClient } from "@apollo/client";
import { ImagingAssetKind } from "elephant-web/gql";
import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchImagingAssetUrl } from "../hooks/imaging-asset-url";
import type { HydratedExamImage } from "../hooks/use-epocus-exam-images";
import {
  fetchBlobWithProgress,
  hasOfflineBlob,
  readSessionBlob,
  writeOfflineBlob,
} from "./cine-asset-store";

export type OfflineCineState = "IDLE" | "STORING" | "STORED" | "FAILED";

export type OfflineCine = {
  playableCount: number;
  state: OfflineCineState;
  storedCount: number;
  makeAvailableOffline: () => void;
};

const OFFLINE_KINDS = [
  ImagingAssetKind.Preview,
  ImagingAssetKind.Cine,
] as const;

const storeAsset = async (
  apolloClient: ApolloClient<object>,
  imagingInstanceId: string,
  kind: ImagingAssetKind,
): Promise<boolean> => {
  if (await hasOfflineBlob(imagingInstanceId, kind)) return true;

  const sessionBlob = readSessionBlob(imagingInstanceId, kind);
  if (sessionBlob) {
    return writeOfflineBlob(imagingInstanceId, kind, sessionBlob);
  }

  const signedUrl = await fetchImagingAssetUrl(
    apolloClient,
    imagingInstanceId,
    kind,
  );
  if (!signedUrl) return false;

  const blob = await fetchBlobWithProgress(signedUrl);
  return writeOfflineBlob(imagingInstanceId, kind, blob);
};

export const useOfflineCine = (images: HydratedExamImage[]): OfflineCine => {
  const apolloClient = useApolloClient();
  const [state, setState] = useState<OfflineCineState>("IDLE");
  const [storedIds, setStoredIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const playableIds = useMemo(
    () =>
      images
        .filter((image) => image.cine?.availability === "READY")
        .map((image) => image.id),
    [images],
  );

  useEffect(() => {
    if (playableIds.length === 0) return;
    let isCancelled = false;

    const check = async () => {
      const stored = await Promise.all(
        playableIds.map(async (id) =>
          (await hasOfflineBlob(id, ImagingAssetKind.Cine)) ? id : null,
        ),
      );
      if (isCancelled) return;
      setStoredIds(new Set(stored.filter((id): id is string => id !== null)));
    };

    check();

    return () => {
      isCancelled = true;
    };
  }, [playableIds]);

  const makeAvailableOffline = useCallback(() => {
    setState("STORING");

    const store = async () => {
      const stored = new Set<string>();
      let anyFailed = false;

      for (const id of playableIds) {
        try {
          const results = await Promise.all(
            OFFLINE_KINDS.map((kind) => storeAsset(apolloClient, id, kind)),
          );
          if (results.every(Boolean)) stored.add(id);
          else anyFailed = true;
        } catch {
          anyFailed = true;
        }
        setStoredIds(new Set(stored));
      }

      setState(anyFailed ? "FAILED" : "STORED");
    };

    store();
  }, [apolloClient, playableIds]);

  return {
    playableCount: playableIds.length,
    state:
      state === "IDLE" &&
      playableIds.length > 0 &&
      storedIds.size === playableIds.length
        ? "STORED"
        : state,
    storedCount: storedIds.size,
    makeAvailableOffline,
  };
};
